import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageCircle,
  Stethoscope,
  Tractor,
  PawPrint,
  Building2,
  Download,
  ChevronRight,
  MapPin,
  Handshake,
} from "lucide-react";

import BiodealLogo from "@/components/BiodealLogo";
import CompactProductFinder from "@/components/CompactProductFinder";
import ProductCard from "@/components/ProductCard";
import SectionLabel from "@/components/SectionLabel";
import SafeImage from "@/components/SafeImage";
import PillarFlipCard from "@/components/PillarFlipCard";
import { AUDIENCES, RESPONSIBILITY_PILLARS } from "@/data/solutions";
import { PRODUCTS } from "@/data/products";
import { buildWaLink, WA_TEMPLATES } from "@/lib/whatsapp";
import { BAH_PRODUCT_CATALOGUE_PDF } from "@/lib/assets";

const HERO_IMG =
  "https://images.pexels.com/photos/3152331/pexels-photo-3152331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600";

// 4 featured products (was 8) per Phase 1A brief.
const FEATURED_IDS = [
  "wormizan-super",
  "biotrim-vet-plus",
  "popvet-total",
  "biosalve-milking-salve",
];

const AUDIENCE_ICON = {
  vets: Stethoscope,
  farmers: Tractor,
  companion: PawPrint,
  partners: Building2,
};

// Merged Training / CMT / Field Support tiles — absorbs the old Field Activity
// gallery so we have a single credible "support" block on Home.
const FIELD_TILES = [
  {
    src: "https://images.pexels.com/photos/6235241/pexels-photo-6235241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
    label: "Veterinary CMT",
  },
  {
    src: "https://images.pexels.com/photos/3152331/pexels-photo-3152331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
    label: "Farm visits",
  },
  {
    src: "https://images.pexels.com/photos/2933243/pexels-photo-2933243.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
    label: "Agrovet education",
  },
  {
    src: "https://images.pexels.com/photos/37748139/pexels-photo-37748139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=820",
    label: "Poultry support",
  },
];

export default function Home() {
  const featured = FEATURED_IDS.map((id) => PRODUCTS.find((p) => p.id === id)).filter(Boolean);

  return (
    <div data-testid="home-page">
      {/* ---------------- 1. HERO ---------------- */}
      <section
        className="relative min-h-[88vh] md:min-h-[92vh] flex items-end overflow-hidden"
        data-testid="hero-section"
      >
        <SafeImage
          src={HERO_IMG}
          alt="East African farmer guiding cattle at golden hour"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-14 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-3">
                <BiodealLogo size="lg" />
                <div className="text-bah-yellow text-[11px] uppercase tracking-[0.28em] font-bold">
                  Biodeal Animal Health
                </div>
              </div>
              <h1
                className="mt-6 sm:mt-8 font-display text-white text-[2rem] leading-[1.08] sm:text-5xl sm:leading-[1.06] md:text-6xl md:leading-[1.04] lg:text-7xl tracking-tight max-w-[18ch] sm:max-w-[22ch] md:max-w-[26ch] lg:max-w-4xl"
                data-testid="hero-headline"
              >
                Responsible Animal Health.{" "}
                <span className="text-bah-yellow">Trusted by Professionals.</span>{" "}
                Built for Farmers.
              </h1>
              <p
                className="mt-5 sm:mt-6 text-white/85 text-base sm:text-lg md:text-xl leading-relaxed max-w-[38ch] sm:max-w-2xl"
                data-testid="hero-subtext"
              >
                Biodeal Animal Health supports veterinarians, livestock
                producers, agrovets and animal caregivers across East Africa
                with quality medicines, practical field-use instruments,
                cold-chain support and technical animal health expertise.
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
                <Link to="/vet-professionals" className="btn-primary" data-testid="hero-primary-cta">
                  Vet Professionals <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/products" className="btn-secondary" data-testid="hero-secondary-cta">
                  Explore Products
                </Link>
                <Link to="/training" className="btn-secondary" data-testid="hero-tertiary-cta">
                  Book Training
                </Link>
              </div>
            </div>

            {/* Hero stats — preserved (verified 25+ years messaging retained). */}
            <div className="lg:col-span-4 grid grid-cols-3 gap-3">
              {[
                { v: "25+", l: "Years Biodeal experience" },
                { v: "9", l: "Solution categories" },
                { v: "E.A.", l: "Field-tested for East Africa" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-4 text-white"
                >
                  <div className="font-display text-2xl md:text-3xl text-bah-yellow leading-none">
                    {s.v}
                  </div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-white/80 leading-snug">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- 2. AUDIENCE PATHING ---------------- */}
      <section className="py-20 md:py-24 bg-bah-paper" data-testid="audiences-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <SectionLabel>Where do you start?</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight tracking-tight">
              A platform built around the people at the front line of animal health.
            </h2>
            <p className="mt-4 text-bah-mute md:text-lg">
              Veterinary professionals first — supported alongside farmers, pet
              caregivers and commercial partners. Products remain accessible to
              everyone directly from the top navigation.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {AUDIENCES.map((a, i) => {
              const Icon = AUDIENCE_ICON[a.id];
              return (
                <article
                  key={a.id}
                  className="card-rise bg-white border border-bah-line rounded-2xl overflow-hidden flex flex-col"
                  data-testid={`audience-card-${a.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <SafeImage
                      src={a.image}
                      alt={a.title}
                      loading={i < 2 ? "eager" : "lazy"}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-bah-ink/70 to-transparent" />
                    <div className="absolute top-3 left-3 h-10 w-10 rounded-full bg-white/90 grid place-content-center text-bah-green">
                      {Icon && <Icon className="h-5 w-5" />}
                    </div>
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="font-display text-white text-xl leading-tight">
                        {a.title}
                      </h3>
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-sm text-bah-mute leading-relaxed">{a.blurb}</p>
                    <Link
                      to={a.cta_to}
                      data-testid={`audience-cta-${a.id}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-bah-green hover:text-bah-orange transition-colors"
                    >
                      {a.cta_label} <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- 3. COMPACT PRODUCT FINDER ---------------- */}
      <section
        className="py-20 md:py-24 bg-bah-paperAlt"
        data-testid="compact-finder-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <SectionLabel>Compact product finder</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-4xl text-bah-ink leading-tight">
              Species → health need → suggested products.
            </h2>
            <p className="mt-4 text-bah-mute md:text-lg max-w-lg">
              A quick 2-step guide from the Biodeal catalogue. For the full
              3-step finder with responsible-use notes, visit{" "}
              <Link to="/vet-professionals" className="text-bah-green underline">
                Vet Professionals
              </Link>
              .
            </p>
          </div>
          <div className="lg:col-span-7">
            <CompactProductFinder />
          </div>
        </div>
      </section>

      {/* ---------------- 4. FEATURED PHARMACEUTICAL INNOVATIONS ---------------- */}
      <section
        className="py-20 md:py-24 bg-bah-paper"
        data-testid="featured-products-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <SectionLabel>Featured pharmaceutical innovations</SectionLabel>
              <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
                From the Biodeal Animal Health catalogue.
              </h2>
              <p className="mt-4 text-bah-mute max-w-xl">
                Real product names, organised by category — no claims beyond
                the catalogue, no invented uses.
              </p>
            </div>
            <Link to="/products" className="btn-ghost-dark" data-testid="featured-explore-all">
              Explore all products <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- 5. STORY PILLARS (PROTECTED — UNCHANGED) ---------------- */}
      <section
        className="relative py-20 md:py-32 bg-bah-ink text-white overflow-hidden"
        data-testid="pillars-section"
      >
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-5">
              <div className="eyebrow !text-bah-yellow">Our story pillars</div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
                Behind every healthy animal is a responsible professional.
              </h2>
              <p className="mt-5 text-white/75 md:text-lg max-w-md">
                Biodeal Animal Health combines medicines, instruments,
                cold-chain support, field education and technical guidance —
                so professionals can serve farmers better.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
              {RESPONSIBILITY_PILLARS.map((p, idx) => (
                <PillarFlipCard key={p.id} pillar={p} index={idx} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- 6. TRAINING / CMT / FIELD SUPPORT ---------------- */}
      <section
        className="py-20 md:py-24 bg-bah-paper"
        data-testid="training-preview"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-3">
              {FIELD_TILES.map((t, i) => (
                <div
                  key={i}
                  className={`relative overflow-hidden rounded-2xl border border-bah-line ${
                    i % 2 === 0 ? "translate-y-4" : ""
                  }`}
                >
                  <SafeImage
                    src={t.src}
                    alt={t.label}
                    loading="lazy"
                    className="w-full h-full object-cover aspect-square"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-bah-ink/90 via-bah-ink/20 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-white font-display text-sm md:text-base leading-tight">
                    {t.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionLabel>Training / CMT / field support</SectionLabel>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-bah-ink leading-tight">
              Support beyond the product.
            </h2>
            <p className="mt-5 text-bah-mute md:text-lg max-w-lg">
              Biodeal Animal Health supports veterinary professionals,
              agrovets and farmers through practical training, product
              education, biosecurity sessions and field engagement.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/training" className="btn-primary" data-testid="training-cmt-cta">
                Book CMT / Training
              </Link>
              <a
                href={buildWaLink(
                  "Hello Biodeal Animal Health, I'd like to request field support."
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
                data-testid="field-support-cta"
              >
                <MessageCircle className="h-4 w-4" /> Request Field Support
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- 7. FINAL ACTION AREA ---------------- */}
      <section
        className="relative py-20 md:py-24 bg-bah-green text-white overflow-hidden"
        data-testid="final-cta-section"
      >
        <div className="absolute inset-0 bg-grain opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="eyebrow !text-bah-yellow">Take the next step</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl leading-[1.05]">
              Where to buy, partner with us, or talk to the team.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/contact"
              data-testid="final-where-to-buy"
              className="rounded-2xl bg-white/10 border border-white/20 p-6 hover:bg-white/15 transition-colors block"
            >
              <MapPin className="h-6 w-6 text-bah-yellow" />
              <div className="mt-4 font-display text-2xl leading-snug">Where to Buy</div>
              <p className="mt-2 text-sm text-white/80">
                Find a stockist near you — send us your product and location and we'll route you.
              </p>
            </Link>
            <Link
              to="/become-a-stockist"
              data-testid="final-partner"
              className="rounded-2xl bg-white/10 border border-white/20 p-6 hover:bg-white/15 transition-colors block"
            >
              <Handshake className="h-6 w-6 text-bah-yellow" />
              <div className="mt-4 font-display text-2xl leading-snug">Partner with Biodeal</div>
              <p className="mt-2 text-sm text-white/80">
                Stockist, distributor, institutional or international — apply for a commercial relationship.
              </p>
            </Link>
            <a
              href={buildWaLink(WA_TEMPLATES.technical_general + " ")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-whatsapp"
              className="rounded-2xl bg-[#25D366]/20 border border-[#25D366]/50 p-6 hover:bg-[#25D366]/30 transition-colors block"
            >
              <MessageCircle className="h-6 w-6 text-[#25D366]" />
              <div className="mt-4 font-display text-2xl leading-snug">Contact / WhatsApp</div>
              <p className="mt-2 text-sm text-white/80">
                Instant conversation with the Biodeal Animal Health technical team.
              </p>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={BAH_PRODUCT_CATALOGUE_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="final-cta-catalogue"
            >
              <Download className="h-4 w-4" /> Download Catalogue
            </a>
            <Link to="/training" className="btn-secondary !border-white !text-white hover:!bg-white/10" data-testid="final-cta-training">
              Book Training
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
